<template>
    <div :style="{ height: chartHeight }">
        <v-chart class="chart" :option="chartOptions" autoresize />
    </div>
</template>

<script>
import { formatDate } from '@utils/dateRangeFormatter.js';
import { colors } from 'quasar';
import { mapGetters } from 'vuex';
const { getPaletteColor } = colors;

import { LineChart } from 'echarts/charts';
import {
    LegendComponent,
    TitleComponent,
    ToolboxComponent,
    TooltipComponent
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';
import { mapState } from 'pinia';
import { useReportsStore } from '@stores/reports.js';
import { useAuthStore } from '@stores/auth';

use([
    CanvasRenderer,
    LineChart,
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    ToolboxComponent
]);

export default {
    name: 'LossChart',
    components: { VChart },
    props: {
        report: {
            type: Object,
            required: true
        },
        comparisonReport: {
            type: Object,
            required: false
        },
        facility: {
            type: Number,
            required: true
        },
        axisType: {
            type: String,
            required: true
        },
        isComparison: {
            type: Boolean,
            required: false,
            default: false
        },
        selectedGroupBy: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            // toggle axis type ('count' or 'duration')
            MAX_ITEMS: 30
        };
    },
    methods: {
        /**
         * Format the duration to two decimal places
         * @param  {Number} duration
         * @return {String} formatted duration upto 2 decimal places
         */
        formatDuration(val) {
            return (val / 60).toFixed(2);
        },
        /**
         * Format the tooltip content
         * @param  {Object[]} params
         * @return {String} formatted tooltip content
         */
        formatTooltipContent(params) {
            const label = params[0]['axisValueLabel'];
            const marker = params[0]['marker'];
            const period = params[0]['seriesName'];
            let unit = this.$t('min');
            if (this.axisType === 'count') {
                unit = this.$t('count');
            }
            const value = `${params[0]['data']} ${unit}`;
            return `<div>${marker} ${label}</div> <div>${period} <b>${value}</b></div>`;
        }
    },
    computed: {
        ...mapGetters({
            getFacility: 'facilities/getFacility'
        }),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get facility object from facility id
         * @return {Object} facility object
         */
        facilityDetail() {
            return this.getFacility(this.facility);
        },
        /**
         * Get the chart definition for the echart component.
         * @return {Object} chart options for echart component
         */
        chartOptions() {
            return {
                grid: {
                    top: 50,
                    bottom: this.comparisonReport ? 70 : 30,
                    outerBoundsMode: 'same',
                    outerBoundsContain: 'axisLabel'
                },
                title: {
                    text: this.chartTitle,
                    left: 5,
                    textStyle: {
                        fontSize: 16
                    }
                },
                legend: this.comparisonReport ? this.legendData : null,
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    },
                    formatter: params => {
                        return this.formatTooltipContent(params);
                    }
                },
                toolbox: {
                    show: true,
                    itemSize: 20,
                    right: 40,
                    feature: {
                        saveAsImage: {
                            name: 'losses_report',
                            title: this.$t('Save as image'),
                            iconStyle: {},
                            icon:
                                // svg path as defined from fontawesome
                                'path://M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm76.45 211.36l-96.42 95.7c-6.65 6.61-17.39 6.61-24.04 0l-96.42-95.7C73.42 337.29 80.54 320 94.82 320H160v-80c0-8.84 7.16-16 16-16h32c8.84 0 16 7.16 16 16v80h65.18c14.28 0 21.4 17.29 11.27 27.36zM377 105L279.1 7c-4.5-4.5-10.6-7-17-7H256v128h128v-6.1c0-6.3-2.5-12.4-7-16.9z'
                        }
                    }
                },
                xAxis: [this.xAxisOptions],
                yAxis: this.yAxisOptions,
                series: this.formattedSeriesData
            };
        },
        /**
         * Construct chart data from report data.
         * @return {Object[]}
         */
        chartData() {
            const chartMap = {};

            const rows = this.report.rows.slice(0, this.MAX_ITEMS);
            // add row values to chart map
            rows.forEach(row => {
                // add row values to chart map with cause id as primary key
                chartMap[row.id] = {
                    name: row.name,
                    nbEvents: {
                        current: row.nbEvents,
                        comparison: 0
                    },
                    totalDuration: {
                        current: row.totalDuration,
                        comparison: 0
                    }
                };
            });

            // convert chart map to array
            const chartData = Object.values(chartMap);

            // sort chart data based on chart type
            if (this.axisType === 'count') {
                chartData.sort(
                    (a, b) => a.nbEvents.current - b.nbEvents.current
                );
            } else {
                chartData.sort(
                    (a, b) => a.totalDuration.current - b.totalDuration.current
                );
            }
            return chartData;
        },
        /**
         * Sorted series data based on x-axis type.
         * @return {Number[]}
         */
        seriesData() {
            const chart = this.chartData;
            if (this.axisType === 'count') {
                return chart.map(data => data.nbEvents);
            } else {
                return chart.map(data => data.totalDuration);
            }
        },
        /**
         * Formatted series data for rendering in chart
         */
        formattedSeriesData() {
            const seriesArr = [];
            const currentSeries = {
                type: 'bar',
                name: this.isComparison
                    ? this.comparisonReportInterval
                    : this.reportInterval,
                data: this.seriesData.map(data =>
                    this.axisType === 'duration'
                        ? this.formatDuration(data.current)
                        : data.current
                ),
                color: getPaletteColor('light-red')
            };
            seriesArr.push(currentSeries);
            return seriesArr;
        },
        /**
         * Get maximum value between current and comparison values for x-axis values
         * @return {Number}  Maximum value between current and comparison values
         */
        maxSeriesValue() {
            return Math.max(
                ...this.seriesData.map(data => data.current),
                ...this.seriesData.map(data => data.comparison)
            );
        },
        /**
         * Get legend data for rendering in chart when comparison report is available
         * eCharts update: The legend data must be the same as the series name or series data
         */
        legendData() {
            const legend = {
                orient: 'horizontal',
                middle: 0,
                bottom: 0
            };
            if (this.isComparison) {
                legend['data'] = [this.comparisonReportInterval];
            } else {
                legend['data'] = [this.reportInterval];
            }
            return legend;
        },
        /**
         * Get formatted date of current report interval in locale format string
         * @return {String} Formatted date of current report interval
         */
        reportInterval() {
            return formatDate(
                this.getDateRange.start,
                this.getDateRange.end,
                this.userLocale
            );
        },
        /**
         * Get formatted date of comparison report interval in locale format string
         * @return {String} Formatted date of comparison report interval
         */
        comparisonReportInterval() {
            return formatDate(
                this.getDateRange.compareStart,
                this.getDateRange.compareEnd,
                this.userLocale
            );
        },
        /**
         * Get the chart title.
         * @return {String}
         */
        chartTitle() {
            // Get title only for comparison chart
            if (this.report.dateInterval && this.comparisonReport) {
                if (this.isComparison) {
                    return this.$t(
                        'Comparison Period - Top {items} {group} - {facility}',
                        {
                            items: this.MAX_ITEMS,
                            group: this.selectedGroupBy,
                            facility: this.facilityDetail?.name
                        }
                    );
                } else {
                    return this.$t(
                        'Current Period - Top {items} {group} - {facility}',
                        {
                            items: this.MAX_ITEMS,
                            group: this.selectedGroupBy,
                            facility: this.facilityDetail?.name
                        }
                    );
                }
            } else {
                return this.$t('Top {items} {group} - {facility}', {
                    items: this.MAX_ITEMS,
                    group: this.selectedGroupBy,
                    facility: this.facilityDetail?.name
                });
            }
        },
        /**
         * Determine label to use for x-axis or tooltips based on the x-axis type selected.
         * @return {String}
         */
        xAxisLabel() {
            if (this.axisType === 'count') {
                return this.$t('Number of occurrence');
            } else {
                return this.$t('Duration (min)');
            }
        },
        /**
         * x-axis options to use base on the x-axis type selected.
         * @return {Object}
         */
        xAxisOptions() {
            const options = {
                type: 'value',
                name: this.xAxisLabel,
                nameLocation: 'middle',
                nameGap: 35,
                min: 0,
                max: 100,
                axisLabel: {
                    color: 'black',
                    formatter(val) {
                        return Math.floor(val);
                    },
                    fontSize: 13
                },
                nameTextStyle: {
                    fontSize: 13,
                    color: 'black'
                }
            };

            // adjust x-axis max value and interval based on axis type
            let maxSeriesData = this.maxSeriesValue;
            if (this.axisType === 'duration') {
                // convert it to minutes
                options['max'] = maxSeriesData / 60;
                options['interval'] = Math.floor(options['max'] / 5);
            } else {
                options['max'] = maxSeriesData;
                options['interval'] = maxSeriesData / 5;
            }

            // to ensure labels fit for small devices, only show min and max value for interval labels
            if (this.$q.screen.lt.sm) {
                options['interval'] = maxSeriesData;
            }
            return options;
        },
        /**
         * Determine y-axis options based on the number of results to plot on the chart.
         * @return {Object}
         */
        yAxisOptions() {
            const names = this.chartData.map(data => data.name);
            const yAxisOptions = {
                type: 'category',
                axisLabel: {
                    color: '#333',
                    fontSize: 13,
                    formatter(val) {
                        return val;
                    },
                    interval: 0
                },
                data: names
            };

            return yAxisOptions;
        },
        /**
         * Computes the height of the chart based on the number of lines in the y-axis data.
         * @returns {string} The height of the chart in pixels.
         */
        chartHeight() {
            const totalLines = this.yAxisOptions.data.length;
            const height = totalLines ? totalLines * 30 + 100 : 0;
            return height > 600 ? `${height}px` : '600px';
        }
    }
};
</script>

<style lang="scss" scoped>
.chart {
    width: calc(100% - 2px);
    padding-bottom: 15px;
}
</style>
